import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from './AuthContext';
import {
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  limit,
  startAfter,
  getDocs,
  Timestamp,
  doc,
  updateDoc,
  QueryDocumentSnapshot,
  DocumentData,
} from 'firebase/firestore';
import { db } from '../firebase';

/** Live window size, and how many more each "Show older" fetches. */
const PAGE_SIZE = 50;

export interface InAppNotification {
  id: string;
  userId: string;
  showId: string;
  type: string;
  title: string;
  body: string;
  data: {
    type: string;
    showId: string;
    targetId: string;
    deepLink: string;
    resourceType?: string;
    isTest?: string;
  };
  read: boolean;
  isTest: boolean;
  createdAt: Timestamp;
  expiresAt: Timestamp;
}

interface NotificationContextType {
  notifications: InAppNotification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  refreshNotifications: () => void;
  /**
   * Cursor-page past the live window: fetches the next PAGE_SIZE older
   * notifications one-shot and appends them to `notifications`. Before this
   * existed the list was silently truncated at 50 - notification 51 was
   * simply unreachable.
   */
  loadOlder: () => Promise<void>;
  hasOlder: boolean;
  loadingOlder: boolean;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

// Hook for show-specific notifications
export const useShowNotifications = (showId: string) => {
  const { profile } = useAuth();
  const [notifications, setNotifications] = useState<InAppNotification[]>([]);
  const [loading, setLoading] = useState(true);

  // Real-time show-specific notifications listener
  useEffect(() => {
    if (!profile?.uid || !showId) {
      setNotifications([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    // Query for user's notifications from this specific show
    const notificationsQuery = query(
      collection(db, 'inAppNotifications'),
      where('userId', '==', profile.uid),
      where('showId', '==', showId),
      orderBy('createdAt', 'desc'),
      limit(50)
    );

    // Set up real-time listener
    const unsubscribe = onSnapshot(
      notificationsQuery,
      (snapshot) => {
        const notificationList: InAppNotification[] = [];
        const now = Timestamp.now();
        
        snapshot.docs.forEach(doc => {
          const data = doc.data();
          
          // Filter out expired notifications
          if (data.expiresAt && data.expiresAt.toMillis() > now.toMillis()) {
            notificationList.push({
              id: doc.id,
              ...data,
            } as InAppNotification);
          }
        });
        
        setNotifications(notificationList);
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching show notifications:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [profile?.uid, showId]);

  // Mark single notification as read
  const markAsRead = useCallback(async (notificationId: string): Promise<void> => {
    try {
      const notificationRef = doc(db, 'inAppNotifications', notificationId);
      await updateDoc(notificationRef, {
        read: true,
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }, []);

  // Mark all show notifications as read
  const markAllAsRead = useCallback(async (): Promise<void> => {
    try {
      const unreadNotifications = notifications.filter(n => !n.read);
      const updatePromises = unreadNotifications.map(notification => 
        updateDoc(doc(db, 'inAppNotifications', notification.id), {
          read: true,
        })
      );
      
      await Promise.all(updatePromises);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  }, [notifications]);

  // Calculate unread count
  const unreadCount = useMemo(() => notifications.filter(n => !n.read).length, [notifications]);

  return {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    refreshNotifications: useCallback(() => setLoading(true), []), // Triggers re-fetch
  };
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { profile } = useAuth();
  const [notifications, setNotifications] = useState<InAppNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Older pages, fetched one-shot with a cursor. The live listener only ever
  // covers the newest PAGE_SIZE; everything beyond it lives here and does not
  // update in real time (old notifications don't change).
  const [olderNotifications, setOlderNotifications] = useState<InAppNotification[]>([]);
  const [hasOlder, setHasOlder] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  // Cursors are raw snapshot docs (not the expiry-filtered list) so paging
  // never re-fetches a window it has already walked past.
  const liveCursorRef = useRef<QueryDocumentSnapshot<DocumentData> | null>(null);
  const olderCursorRef = useRef<QueryDocumentSnapshot<DocumentData> | null>(null);

  // Real-time notifications listener
  useEffect(() => {
    // A different user starts from a fresh window.
    setOlderNotifications([]);
    setHasOlder(false);
    liveCursorRef.current = null;
    olderCursorRef.current = null;

    if (!profile?.uid) {
      setNotifications([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    // Query for user's notifications
    const notificationsQuery = query(
      collection(db, 'inAppNotifications'),
      where('userId', '==', profile.uid),
      orderBy('createdAt', 'desc'),
      limit(PAGE_SIZE)
    );

    // Set up real-time listener
    const unsubscribe = onSnapshot(
      notificationsQuery,
      (snapshot) => {
        const notificationList: InAppNotification[] = [];
        const now = Timestamp.now();

        snapshot.docs.forEach(doc => {
          const data = doc.data();

          // Filter out expired notifications
          if (data.expiresAt && data.expiresAt.toMillis() > now.toMillis()) {
            notificationList.push({
              id: doc.id,
              ...data,
            } as InAppNotification);
          }
        });

        liveCursorRef.current = snapshot.docs[snapshot.docs.length - 1] ?? null;
        // Once older pages exist, their last fetch owns the hasOlder verdict.
        if (!olderCursorRef.current) {
          setHasOlder(snapshot.docs.length === PAGE_SIZE);
        }
        setNotifications(notificationList);
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error('Error fetching notifications:', err);
        setLoading(false);
        setError('Failed to load notifications');
      }
    );

    return () => unsubscribe();
  }, [profile?.uid]);

  // Fetch the next page of older notifications, one-shot.
  const loadOlder = useCallback(async (): Promise<void> => {
    if (!profile?.uid) return;
    const cursor = olderCursorRef.current ?? liveCursorRef.current;
    if (!cursor) return;

    setLoadingOlder(true);
    try {
      const olderQuery = query(
        collection(db, 'inAppNotifications'),
        where('userId', '==', profile.uid),
        orderBy('createdAt', 'desc'),
        startAfter(cursor),
        limit(PAGE_SIZE)
      );
      const snapshot = await getDocs(olderQuery);
      const now = Timestamp.now();
      const fetched: InAppNotification[] = [];

      snapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.expiresAt && data.expiresAt.toMillis() > now.toMillis()) {
          fetched.push({ id: doc.id, ...data } as InAppNotification);
        }
      });

      olderCursorRef.current = snapshot.docs[snapshot.docs.length - 1] ?? olderCursorRef.current;
      setOlderNotifications(prev => [...prev, ...fetched]);
      setHasOlder(snapshot.docs.length === PAGE_SIZE);
    } catch (err) {
      console.error('Error loading older notifications:', err);
    } finally {
      setLoadingOlder(false);
    }
  }, [profile?.uid]);

  // Live window plus older pages, deduped by id - a new arrival shifts the
  // live window down, so its tail can overlap the head of the older pages.
  const allNotifications = useMemo(() => {
    if (olderNotifications.length === 0) return notifications;
    const liveIds = new Set(notifications.map(n => n.id));
    return [...notifications, ...olderNotifications.filter(n => !liveIds.has(n.id))];
  }, [notifications, olderNotifications]);

  // Mark single notification as read
  const markAsRead = useCallback(async (notificationId: string): Promise<void> => {
    try {
      const notificationRef = doc(db, 'inAppNotifications', notificationId);
      await updateDoc(notificationRef, {
        read: true,
      });
      // Older pages are one-shot fetches, so reflect the write locally.
      setOlderNotifications(prev =>
        prev.some(n => n.id === notificationId)
          ? prev.map(n => (n.id === notificationId ? { ...n, read: true } : n))
          : prev
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }, []);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async (): Promise<void> => {
    try {
      const unreadNotifications = allNotifications.filter(n => !n.read);
      const updatePromises = unreadNotifications.map(notification =>
        updateDoc(doc(db, 'inAppNotifications', notification.id), {
          read: true,
        })
      );

      await Promise.all(updatePromises);
      setOlderNotifications(prev => prev.map(n => (n.read ? n : { ...n, read: true })));
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  }, [allNotifications]);

  // Force refresh notifications (for testing or manual refresh)
  const refreshNotifications = useCallback((): void => {
    // The real-time listener will automatically update, but we can force a re-render
    setLoading(true);
    // Loading state will be updated by the onSnapshot listener
  }, []);

  // Calculate unread count
  const unreadCount = useMemo(() => allNotifications.filter(n => !n.read).length, [allNotifications]);

  const contextValue: NotificationContextType = {
    notifications: allNotifications,
    unreadCount,
    loading,
    error,
    markAsRead,
    markAllAsRead,
    refreshNotifications,
    loadOlder,
    hasOlder,
    loadingOlder,
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  );
};